import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { ArrowsClockwise, Wallet, Eye, ListDashes, Broadcast, Drop, MapPin } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataToolbar } from '../components/DataToolbar';
import { DataTable } from '../components/DataTable';
import { DataPagination } from '../components/DataPagination';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { WalletTxnSummaryTab } from '../components/WalletTxnSummaryTab';

const fmtAmt = (v) => {
  const n = Number(v || 0);
  if (!isFinite(n)) return '-';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="flex flex-col gap-1 py-2 border-b border-slate-100 last:border-b-0">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {label}
      </span>
      <span className="text-sm text-[#1C1917]" style={{ fontFamily: mono ? 'JetBrains Mono, monospace' : 'Plus Jakarta Sans, sans-serif' }}>
        {value || '-'}
      </span>
    </div>
  );
}

export default function WalletTransactionsPage() {
  const { isSuperAdmin } = useAuth();
  const { entities } = useTenant();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchTrans = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      if (isSuperAdmin && entityFilter && entityFilter !== 'all') {
        params.set('entity_id', entityFilter);
      }
      const { data } = await axios.get(`${API_URL}/api/wallet-transactions?${params.toString()}`, {
        withCredentials: true,
        headers,
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number(data?.total || 0));
      setTotalPages(Number(data?.total_pages || 1));
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load wallet transactions'));
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, entityFilter, isSuperAdmin]);

  useEffect(() => { fetchTrans(); }, [fetchTrans]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.trn_ref_no, r.card_no, r.narration, r.maker_id, r.entity_name, r.campus_name, r.store_name, r.channel]
        .some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [rows, search]);

  const columns = useMemo(() => [
    { key: 'trn_ref_no', label: 'Ref',       width: 'minmax(140px,1.2fr)', mono: true, tone: 'strong', render: (r) => r.trn_ref_no || `#${r.id}` },
    { key: 'card_no',    label: 'Card',      width: 'minmax(160px,1.2fr)', mono: true },
    { key: 'narration',  label: 'Narration', width: 'minmax(220px,2fr)',   render: (r) => {
      const amt = Number(r.amount || 0);
      // Color the narration to mirror the amount sign so the row reads as
      // a money-in / money-out event at a glance.
      const tone = amt < 0 ? 'text-rose-700' : (amt > 0 ? 'text-emerald-700' : '');
      return (
        <span className={`inline-flex items-center gap-2 font-medium ${tone}`}>
          <span className="truncate">{r.narration || '—'}</span>
          {r.special_offer === 'Y' && (
            <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-amber-100 text-amber-800 group-hover:bg-[#EDE6D8] group-hover:text-[#1C1917]">OFFER</span>
          )}
        </span>
      );
    } },
    { key: 'amount', label: 'Amount', width: 'minmax(140px,1.1fr)', mono: true, align: 'right', tone: 'strong', render: (r) => {
      // Signed amount with explicit + / − so debits are unambiguous; the
      // upstream sometimes emits a positive `amount` for refills, so we
      // also lean on `type === "debit"` as a tiebreaker.
      const raw = Number(r.amount || 0);
      const isDebit = raw < 0 || String(r.type || '').toLowerCase() === 'debit';
      const abs = Math.abs(raw);
      const display = `${isDebit ? '−' : '+'} ${fmtAmt(abs)}`;
      const cls = isDebit ? 'text-rose-700 font-semibold' : 'text-emerald-700 font-semibold';
      return <span className={cls} data-testid={`trans-amt-${r.id || r.trn_ref_no || ''}`}>{display}</span>;
    } },
    { key: 'currency',   label: 'Currency',  width: 'minmax(80px,0.7fr)',  mono: true, tone: 'muted' },
    { key: 'trn_date',   label: 'Date',      width: 'minmax(170px,1.3fr)', mono: true, tone: 'muted', render: (r) => fmtDate(r.trn_date) },
  ], []);

  return (
    <div className="space-y-3 pb-6" data-testid="wallet-transactions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Wallet Transactions
        </h1>
        <Button
          variant="outline"
          onClick={() => { setPage(1); fetchTrans(); }}
          data-testid="refresh-trans"
          className="border-[#EDE6D8]"
        >
          <ArrowsClockwise size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="all" className="space-y-3">
        <TabsList className="bg-white border border-[#EDE6D8] rounded-xl p-1 h-auto flex flex-wrap">
          <TabsTrigger value="all" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="wt-tab-all">
            <ListDashes size={14} weight="duotone" /> All Logs
          </TabsTrigger>
          <TabsTrigger value="recharge" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="wt-tab-recharge">
            <Broadcast size={14} weight="duotone" /> By Channel
          </TabsTrigger>
          <TabsTrigger value="refill" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="wt-tab-refill">
            <Drop size={14} weight="duotone" /> By Refills
          </TabsTrigger>
          <TabsTrigger value="campus" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="wt-tab-campus">
            <MapPin size={14} weight="duotone" /> By Campus
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-3">
      {/* Toolbar */}
      <DataToolbar
        value={search}
        onChange={setSearch}
        placeholder="Search ref, card, narration…"
        testId="trans-toolbar"
        onClearAll={() => { setSearch(''); setEntityFilter('all'); }}
        filters={isSuperAdmin ? [{
          key: 'entity',
          label: 'Entity',
          value: entityFilter,
          onChange: (v) => { setEntityFilter(v); setPage(1); },
          options: [{ value: 'all', label: 'All entities' }, ...entities.map((e) => ({ value: e.id, label: e.entity_name }))],
        }] : []}
      />

      <DataTable
        columns={columns}
        rows={filteredRows}
        loading={loading}
        rowKey={(r) => `${r.id || r.trn_ref_no || ''}-${r.trn_date || ''}`}
        onRowClick={(r) => setSelected(r)}
        emptyIcon={<Wallet size={20} className="text-slate-500" />}
        emptyTitle="No wallet transactions"
        emptyText={search ? 'Try a different search term.' : 'Transactions will appear here as wallets are recharged or used.'}
        testIdPrefix="trans"
        actionRender={(r) => (
          <span className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setSelected(r); }}
              className="text-[#0A2540] hover:text-[#0A2540] hover:bg-[#0A2540]/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
              data-testid={`view-trans-${r.id || r.trn_ref_no || ''}`}
              aria-label="View transaction"
            >
              <Eye size={14} />
            </Button>
          </span>
        )}
      />

      <DataPagination
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        testId="trans-pagination"
      />
        </TabsContent>

        <TabsContent value="recharge">
          <WalletTxnSummaryTab mode="recharge" />
        </TabsContent>

        <TabsContent value="refill">
          <WalletTxnSummaryTab mode="refill" />
        </TabsContent>

        <TabsContent value="campus">
          <WalletTxnSummaryTab mode="campus" />
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-lg" data-testid="trans-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Transaction Detail</DialogTitle>
            <DialogDescription className="sr-only">Full details of the selected wallet transaction</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-1">
              <div className="grid grid-cols-2 gap-4">
                <DetailRow label="Reference" value={selected.trn_ref_no} mono />
                <DetailRow label="Channel" value={selected.channel} />
              </div>
              <DetailRow label="Card" value={selected.card_no} mono />
              <DetailRow label="Narration" value={selected.narration} />
              <div className="grid grid-cols-2 gap-4">
                <DetailRow
                  label="Amount"
                  value={(() => {
                    const raw = Number(selected.amount || 0);
                    const isDebit = raw < 0 || String(selected.type || '').toLowerCase() === 'debit';
                    const sign = isDebit ? '−' : '+';
                    const cls = isDebit ? 'text-rose-700 font-semibold' : 'text-emerald-700 font-semibold';
                    return (
                      <span className={cls}>{sign} {fmtAmt(Math.abs(raw))} {selected.currency || ''}</span>
                    );
                  })()}
                  mono
                />
                <DetailRow label="Special Offer" value={selected.special_offer === 'Y' ? 'Yes' : 'No'} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DetailRow label="Opening Bal" value={fmtAmt(selected.wallet_open_bal)} mono />
                <DetailRow label="Closing Bal" value={fmtAmt(selected.wallet_close_bal)} mono />
              </div>
              <DetailRow label="Date" value={fmtDate(selected.trn_date)} mono />
              <div className="grid grid-cols-2 gap-4">
                {isSuperAdmin ? (
                  <DetailRow label="Entity" value={selected.entity_name} />
                ) : null}
                <DetailRow label="Campus" value={selected.campus_name} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DetailRow label="Store" value={selected.store_name} />
                <DetailRow label="Maker" value={selected.maker_id} mono />
              </div>
              <DetailRow label="Payer Mobile" value={selected.payer_mobile} mono />
              {selected.third_part_trn_id && (
                <DetailRow label="3rd Party Ref" value={selected.third_part_trn_id} mono />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
